import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const INDUSTRIES = [
  "Photography & Video", "Design & Creative", "Web / App Development", "Marketing & Advertising Agency",
  "Consulting", "Coaching & Training", "E-commerce & Retail", "SaaS & Technology", "Event Planning",
  "Real Estate", "Construction & Trades", "Home Services (cleaning, landscaping)", "Health & Wellness",
  "Beauty & Salon", "Fitness & Personal Training", "Food, Catering & Hospitality", "Legal Services",
  "Accounting & Bookkeeping", "Education & Tutoring", "Writing & Content", "Music & Entertainment",
  "Interior Design", "Architecture", "Automotive", "IT & Managed Services", "Logistics & Transportation",
  "Manufacturing", "Nonprofit", "Professional Services", "Freelance / Creative", "Other",
];
const SIZES = ["Just me", "1-10", "11-50"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", industry: "Consulting", size: "1-10", offering: "", target_customers: "", differentiator: "", goals: "", home_airport: "", seed_demo: true });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/onboarding", form);
      setUser(data);
      toast.success("Workspace ready! Welcome to BizForge AI.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg animate-fade-up">
        <div className="mb-8 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary"><Zap className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} /></div>
          <span className="font-display text-lg font-bold">BizForgeAI</span>
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Let's set up your business, {user?.name?.split(" ")[0]}.</h1>
        <p className="mt-2 text-muted-foreground">The more your AI co-pilot knows, the more tailored and trustworthy its help becomes — across insights, outreach, and trip planning.</p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="biz-name">Business name</Label>
            <Input id="biz-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="onboarding-name-input" placeholder="e.g. Nimbus Consulting" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Industry</Label>
              <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                <SelectTrigger data-testid="onboarding-industry-select"><SelectValue /></SelectTrigger>
                <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Team size</Label>
              <Select value={form.size} onValueChange={(v) => setForm({ ...form, size: v })}>
                <SelectTrigger data-testid="onboarding-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="offering">What does your business offer?</Label>
            <Textarea id="offering" value={form.offering} onChange={(e) => setForm({ ...form, offering: e.target.value })} data-testid="onboarding-offering-input" placeholder="e.g. I shoot weddings and brand campaigns, and sell edited photo/video packages plus prints." rows={3} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="target">Who are your ideal clients or customers?</Label>
            <Textarea id="target" value={form.target_customers} onChange={(e) => setForm({ ...form, target_customers: e.target.value })} data-testid="onboarding-target-input" placeholder="e.g. Engaged couples and boutique fashion brands in the Austin area." rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edge">What sets you apart? Your specialties or edge</Label>
            <Textarea id="edge" value={form.differentiator} onChange={(e) => setForm({ ...form, differentiator: e.target.value })} data-testid="onboarding-differentiator-input" placeholder="e.g. Documentary-style storytelling, 48-hour turnaround, and travel-ready for destination shoots." rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="goals">Your main goal right now</Label>
            <Textarea id="goals" value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} data-testid="onboarding-goals-input" placeholder="e.g. Grow monthly revenue 30% and reduce time on admin." rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="home_airport">Your home airport or city <span className="font-normal text-muted-foreground">(helps the AI plan your travel)</span></Label>
            <Input id="home_airport" value={form.home_airport} onChange={(e) => setForm({ ...form, home_airport: e.target.value })} data-testid="onboarding-home-airport-input" placeholder="e.g. Austin or AUS" />
          </div>
          <label className="flex items-center gap-3 rounded-xl border border-border bg-secondary/40 p-3.5 cursor-pointer">
            <Checkbox checked={form.seed_demo} onCheckedChange={(v) => setForm({ ...form, seed_demo: !!v })} data-testid="onboarding-demo-checkbox" />
            <span className="text-sm">Load sample data so I can explore right away</span>
          </label>
          <Button type="submit" className="w-full group" disabled={loading} data-testid="onboarding-submit-button">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Enter my workspace <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </form>
      </div>
    </div>
  );
}
